import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  appealFeedPost,
  checkFusha,
  createFeedPost,
  fetchFeedEligibility,
  fetchMyFeedPosts,
  updateFeedPost,
} from "../../api";
import { normalize } from "../typing/arabicText";
import { arabicDigits, arabicRelativeTime } from "../../utils/arabicNumbers";
import FeedLockedCard from "./FeedLockedCard";
import FushaFeedback from "./FushaFeedback";
import MajlisHeader from "./MajlisHeader";
import HandleSetupCard from "./HandleSetupCard";
import useArabicFieldRemap from "../typing/useArabicFieldRemap";

const DRAFT_KEY = "majlis.submit.draft";
const CHECK_DEBOUNCE_MS = 600;

/**
 * MajlisSubmitPage — /majlis/submit, after design/majlis-submit-screen.html.
 *
 * The heart of the page is the live fuṣḥā editor: as the user types, a
 * debounced call to the FREE deterministic checker (POST /api/feed/check/)
 * flags dialect words with a wavy underline, drives the سلامة الفصحى gate
 * bar, and fills the مساعد الفصحى sidebar with curated fuṣḥā alternatives
 * (استبدل replaces in place). Tier-A flags block publishing; Tier-B warn.
 * The authoritative (metered) LLM check still runs once, at publish.
 *
 * Flagged rendering uses the classic backdrop technique: a mirrored div
 * renders the highlighted text behind a textarea whose own text is
 * transparent (caret stays visible) — contentEditable + Arabic IME is not a
 * fight worth having.
 *
 * Honest-elements: the mockup's community picker, flairs, B/I/❝ tools,
 * auto-tashkeel, and title input are omitted (no backend / plain-text,
 * body-only posts). Drafts in the sidebar are the user's REAL rejected
 * posts (edit-and-resubmit keeps the same post id + coaching thread).
 */
export default function MajlisSubmitPage({ currentUser, isAdmin = false, onOpenTyping, onOpenAdmin }) {
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [body, setBody] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}").body || "";
    } catch {
      return "";
    }
  });
  const [savedAt, setSavedAt] = useState(null);
  const [liveOn, setLiveOn] = useState(true);
  const [check, setCheck] = useState(null); // last /check/ response
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);
  const [rejectedPostId, setRejectedPostId] = useState(null);
  const [rejectedStage, setRejectedStage] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [publishedWithNotes, setPublishedWithNotes] = useState(false);
  const { remapOn, toggleRemap, remapKeyDown } = useArabicFieldRemap();
  const backdropRef = useRef(null);
  const textareaRef = useRef(null);

  const loadEligibility = useCallback(() => {
    fetchFeedEligibility().then(setEligibility).catch(() => {});
  }, []);

  useEffect(() => {
    loadEligibility();
    fetchMyFeedPosts({ status: "rejected" })
      .then((page) => setDrafts(page.results || []))
      .catch(() => {});
  }, [loadEligibility]);

  // Autosave to localStorage (the sidebar drafts are server-side rejected
  // posts; the in-progress draft lives client-side until first submit).
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ body, ts: Date.now() }));
        if (body.trim()) setSavedAt(Date.now());
      } catch {
        /* private browsing */
      }
    }, 800);
    return () => clearTimeout(t);
  }, [body]);

  // Debounced live check.
  useEffect(() => {
    if (!liveOn || !body.trim() || !eligibility?.can_post) {
      setCheck(null);
      return undefined;
    }
    const t = setTimeout(() => {
      checkFusha(normalize(body)).then(setCheck).catch(() => {});
    }, CHECK_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [body, liveOn, eligibility]);

  const flags = useMemo(() => (check?.flags ?? []), [check]);
  const blockingFlags = flags.filter((f) => f.blocking);
  const warningFlags = flags.filter((f) => !f.blocking);
  const scorePct = body.trim() ? (check ? check.score_pct : 100) : 0;
  const maxChars = eligibility?.limits?.post_max_chars || 500;
  const minChars = eligibility?.limits?.post_min_chars || 15;
  const lengthOk = body.trim().length >= minChars && body.trim().length <= maxChars;
  const canPublish =
    lengthOk && !busy && (!liveOn || !check || check.can_publish) && blockingFlags.length === 0;

  // Backdrop segments: body split on whitespace, flagged surfaces underlined.
  const segments = useMemo(() => {
    const surfaces = new Map(flags.map((f) => [f.surface, f]));
    return body.split(/(\s+)/).map((tok, i) => {
      const clean = tok.replace(/^[^؀-ۿ]+|[^؀-ۿ]+$/g, "");
      const flag = surfaces.get(clean);
      return { key: i, text: tok, flag };
    });
  }, [body, flags]);

  const replaceWord = (surface, replacement) => {
    setBody((b) => b.split(surface).join(replacement));
    textareaRef.current?.focus();
  };

  const loadDraft = (draft) => {
    setBody(draft.body);
    setRejectedPostId(draft.id);
    setRejectedStage(null);
    setFeedback(null);
  };

  const publish = async () => {
    const text = normalize(body).trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const data = rejectedPostId
        ? await updateFeedPost(rejectedPostId, text)
        : await createFeedPost(text);
      if (data.post.status === "published") {
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch { /* ignore */ }
        if (data.feedback) {
          // Published, but the checker left private coaching (borderline
          // pass, spelling/grammar notes). Show it — silently discarding it
          // made "published with notes" look identical to "published clean".
          setBody("");
          setFeedback(data.feedback);
          setPublishedWithNotes(true);
          setRejectedPostId(null);
          setRejectedStage(null);
        } else {
          navigate("/majlis");
        }
      } else {
        setFeedback(data.feedback);
        setRejectedPostId(data.post.id);
        setRejectedStage((data.feedback && data.feedback.stage) || null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const appeal = async () => {
    if (!rejectedPostId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const data = await appealFeedPost(rejectedPostId);
      if (data.post.status === "published") {
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch { /* ignore */ }
        if (data.feedback) {
          setBody("");
          setFeedback(data.feedback);
          setPublishedWithNotes(true);
          setRejectedPostId(null);
          setRejectedStage(null);
        } else {
          navigate("/majlis");
        }
      } else {
        setFeedback(data.feedback);
        setRejectedStage((data.feedback && data.feedback.stage) || null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const gateState = !body.trim()
    ? { text: "اكتبْ لتبدأ التحليل", color: "var(--mj-muted)" }
    : blockingFlags.length > 0
    ? {
        text:
          blockingFlags.length === 1
            ? "كلمةٌ عامّيّةٌ واحدة — اخترْ بديلًا فصيحًا لتنشر"
            : `${arabicDigits(blockingFlags.length)} كلماتٍ عامّيّة — اخترْ بدائلَ فصيحةً لتنشر`,
        color: "var(--mj-flag)",
      }
    : warningFlags.length > 0
    ? { text: "كلماتٌ مشتبَهة — ستُراجَع عند النشر", color: "var(--mj-gold)" }
    : !lengthOk
    ? { text: "أكمِلْ جملةً مفيدة", color: "var(--mj-muted)" }
    : { text: "١٠٠٪ — جاهزٌ للنشر ❖", color: "var(--mj-gold)" };

  const typingLocked = eligibility && !eligibility.can_post && eligibility.reason === "typing_incomplete";
  const needsHandle = eligibility && !eligibility.can_post && eligibility.reason === "no_handle";
  const otherBlocked =
    eligibility && !eligibility.can_post && !typingLocked && !needsHandle;

  const editorFont = {
    fontFamily: "'Amiri', serif",
    fontSize: "22px",
    lineHeight: "2.3",
  };

  return (
    <div dir="rtl" lang="ar" className="majlis-scope min-h-screen font-plex">
      <MajlisHeader
        currentUser={{ ...(currentUser || {}), ...(eligibility || {}) }}
        isAdmin={isAdmin}
        onOpenAdmin={onOpenAdmin}
        crumbs="إنشاء منشور"
      />

      <div className="max-w-[1060px] mx-auto px-4 pt-6 pb-24 grid grid-cols-1 min-[920px]:grid-cols-[minmax(0,1fr)_312px] gap-6">
        <main className="min-w-0">
          <h1 className="font-scheherazade font-bold text-[26px] mb-1">أنشئ منشورًا</h1>
          <p className="text-[13.5px] mb-4" style={{ color: "var(--mj-muted)" }}>
            اكتبْ بالفصحى وحدَها —{" "}
            <em className="not-italic" style={{ color: "var(--mj-gold)" }}>
              لن يُنشَرَ المنشورُ حتى تكتملَ سلامةُ الفصحى
            </em>
          </p>

          {typingLocked && (
            <div className="dark-scope">
              <FeedLockedCard typingGate={eligibility.typing_gate} onOpenTyping={onOpenTyping} />
            </div>
          )}
          {needsHandle && <HandleSetupCard onDone={loadEligibility} />}
          {otherBlocked && (
            <p className="text-sm py-6 text-center" style={{ color: "var(--mj-muted)" }}>
              لا يمكنك النشر الآن.
            </p>
          )}

          {eligibility?.can_post && (
            <div
              className="rounded-[14px] p-5"
              style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
            >
              {/* toolbar: the live-check toggle (✓ع) is the one real tool */}
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => setLiveOn((v) => !v)}
                  className="w-[34px] h-[34px] rounded-[9px] grid place-items-center text-[13px] font-bold"
                  style={
                    liveOn
                      ? {
                          background: "var(--mj-gold-soft)",
                          color: "var(--mj-gold)",
                          border: "1px solid var(--mj-gold-line)",
                        }
                      : { color: "var(--mj-muted)", border: "1px solid transparent" }
                  }
                  title="التدقيق الفصيح أثناء الكتابة"
                  aria-pressed={liveOn}
                >
                  ✓ع
                </button>
                <button
                  onClick={toggleRemap}
                  className="w-[34px] h-[34px] rounded-[9px] grid place-items-center text-[13px] font-bold"
                  style={
                    remapOn
                      ? {
                          background: "var(--mj-gold-soft)",
                          color: "var(--mj-gold)",
                          border: "1px solid var(--mj-gold-line)",
                        }
                      : { color: "var(--mj-muted)", border: "1px solid transparent" }
                  }
                  title="اكتب عربيًّا على أيّ لوحة مفاتيح — كما تعلّمتَ في دروس الطباعة"
                  aria-pressed={remapOn}
                >
                  ⌨ع
                </button>
                <span className="text-[12px]" style={{ color: "var(--mj-muted)" }}>
                  {remapOn
                    ? liveOn ? "لوحةُ المفاتيحِ العربيّةُ والتدقيقُ يعملان" : "لوحةُ المفاتيحِ العربيّةُ تعمل"
                    : liveOn ? "التدقيق الفصيح يعمل أثناء الكتابة" : "التدقيق الفصيح متوقّف"}
                </span>
                <span className="ms-auto text-[11px]" style={{ color: "var(--mj-muted)" }}>
                  {arabicDigits(body.length)} / {arabicDigits(maxChars)}
                </span>
              </div>

              {/* the editor: mirrored backdrop (highlights) + transparent textarea */}
              <div className="relative">
                <div
                  ref={backdropRef}
                  aria-hidden="true"
                  className="absolute inset-0 rounded-xl px-5 py-4 overflow-auto whitespace-pre-wrap break-words pointer-events-none"
                  style={{
                    ...editorFont,
                    color: "var(--mj-body)",
                    // the backdrop paints the box AND the glyphs…
                    background: "var(--mj-raised)",
                    border: "1px solid var(--mj-line)",
                  }}
                >
                  {segments.map((seg) =>
                    seg.flag ? (
                      <span
                        key={seg.key}
                        style={{
                          color: seg.flag.blocking ? "var(--mj-flag)" : "var(--mj-gold)",
                          textDecoration: `underline wavy ${
                            seg.flag.blocking ? "var(--mj-flag)" : "var(--mj-gold)"
                          } 1.5px`,
                          textUnderlineOffset: "6px",
                        }}
                      >
                        {seg.text}
                      </span>
                    ) : (
                      <span key={seg.key}>{seg.text}</span>
                    )
                  )}
                </div>
                <textarea
                  ref={textareaRef}
                  dir="rtl"
                  lang="ar"
                  rows={7}
                  value={body}
                  onChange={(e) => { setBody(e.target.value); setPublishedWithNotes(false); }}
                  onKeyDown={(e) => remapKeyDown(e, setBody)}
                  onScroll={(e) => {
                    if (backdropRef.current) backdropRef.current.scrollTop = e.target.scrollTop;
                  }}
                  placeholder="اُكتُبْ قِصَّتَكَ أَوْ سُؤَالَكَ بِالفُصْحَى…"
                  className="relative w-full rounded-xl px-5 py-4 min-h-[220px] outline-none resize-y"
                  style={{
                    ...editorFont,
                    // …while the textarea is invisible glass on top: only the
                    // caret and selection are its own. Transparent border
                    // keeps its metrics identical to the backdrop's.
                    color: "transparent",
                    caretColor: "var(--mj-gold)",
                    background: "transparent",
                    border: "1px solid transparent",
                    zIndex: 1,
                  }}
                />
              </div>

              {/* fusha gate */}
              <div className="mt-4">
                <div className="flex justify-between items-center text-[13px] mb-2">
                  <span style={{ color: "var(--mj-muted)" }}>سلامةُ الفصحى</span>
                  <span className="font-bold" style={{ color: gateState.color }}>
                    {gateState.text}
                  </span>
                </div>
                <div className="h-[3px] rounded-[2px] relative" style={{ background: "var(--mj-line)" }}>
                  <div
                    className="absolute top-0 h-full rounded-[2px]"
                    style={{
                      insetInlineStart: 0,
                      width: `${scorePct}%`,
                      background: "linear-gradient(270deg, var(--mj-gold), #8f7222)",
                      boxShadow: scorePct === 100 ? "0 0 12px rgba(201,162,58,.5)" : "none",
                      transition: "width .3s ease",
                    }}
                  />
                </div>
              </div>

              {error && (
                <p className="text-xs mt-3 px-1" style={{ color: "var(--mj-flag)" }}>{error}</p>
              )}
              {publishedWithNotes && (
                <div
                  className="flex items-center gap-3 rounded-[10px] px-3.5 py-2.5 mt-3 text-[13px] font-semibold"
                  style={{
                    background: "var(--mj-note-bg)",
                    border: "1px solid var(--mj-ink-6)",
                    color: "var(--mj-ink-4)",
                  }}
                  role="status"
                >
                  ✓ نُشِرَ منشورُك — راجع الملاحظاتِ أدناه لتتقنَ القادم.
                  <button
                    onClick={() => navigate("/majlis")}
                    className="ms-auto rounded-full px-4 py-1 text-[12.5px] font-bold shrink-0"
                    style={{ background: "var(--mj-gold)", color: "var(--mj-bg)" }}
                  >
                    إلى المجلس
                  </button>
                </div>
              )}
              <div className="dark-scope">
                <FushaFeedback
                  feedback={feedback}
                  onApplySuggestion={(t, s) => setBody((b) => b.replace(t, s))}
                />
              </div>
              {rejectedPostId && rejectedStage === "local" && feedback && (
                <button
                  onClick={appeal}
                  disabled={busy}
                  className="text-xs rounded-lg px-3 py-1.5 mt-2 disabled:opacity-50"
                  style={{ color: "var(--mj-muted)", border: "1px solid var(--mj-line)" }}
                >
                  أعتقد أنّها فصيحة — شغّل التدقيق الكامل
                </button>
              )}

              {/* footer */}
              <div
                className="flex items-center gap-3.5 mt-4 pt-4 flex-wrap"
                style={{ borderTop: "1px solid var(--mj-line)" }}
              >
                <span className="text-[12.5px]" style={{ color: "var(--mj-muted)" }}>
                  {savedAt
                    ? `يُحفَظُ تلقائيًّا · آخرُ حفظٍ ${arabicRelativeTime(new Date(savedAt).toISOString())}`
                    : "يُحفَظُ تلقائيًّا"}
                </span>
                <button
                  onClick={publish}
                  disabled={!canPublish}
                  className="ms-auto rounded-full px-8 py-2 text-[14.5px] font-bold flex items-center gap-2"
                  style={{
                    background: "var(--mj-gold)",
                    color: "var(--mj-bg)",
                    opacity: canPublish ? 1 : 0.4,
                    cursor: canPublish ? "pointer" : "not-allowed",
                  }}
                >
                  {busy ? "جارٍ الفحص…" : "انشر ❖"}
                </button>
              </div>
            </div>
          )}
        </main>

        {/* sidebar */}
        <aside className="sticky top-[76px] self-start flex-col gap-4 hidden min-[920px]:flex">
          {/* live fusha helper */}
          <div
            className="rounded-[14px] px-4 py-3.5"
            style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
          >
            <SideTitle>مساعدُ الفصحى</SideTitle>
            {flags.length === 0 && (
              <p className="text-[12px] leading-[1.9]" style={{ color: "var(--mj-muted)" }}>
                تُحلَّلُ كتابتُك أثناءَ الطباعةِ — تظهرُ الكلماتُ العامّيّةُ هنا مع بدائلِها الفصيحة.
              </p>
            )}
            {flags.map((flag) => (
              <div key={flag.marker} className="mb-3 last:mb-0">
                <span
                  className="font-arabic text-[24px] line-through"
                  style={{ color: flag.blocking ? "var(--mj-flag)" : "var(--mj-gold)" }}
                >
                  {flag.surface}
                </span>
                <div className="flex flex-col gap-1.5 mt-2">
                  {flag.suggestions.map((sug) => (
                    <button
                      key={sug.text}
                      onClick={() => replaceWord(flag.surface, sug.text)}
                      className="flex items-center justify-between rounded-[10px] px-3 py-2"
                      style={{ background: "var(--mj-raised)", border: "1px solid var(--mj-line)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--mj-gold-line)")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--mj-line)")}
                    >
                      <b className="font-arabic text-[19px]" style={{ color: "var(--mj-gold)" }}>
                        {sug.text}
                      </b>
                      <span className="text-[11px]" style={{ color: "var(--mj-muted)" }}>
                        {sug.note || "استبدِل"}
                      </span>
                    </button>
                  ))}
                </div>
                {flag.note_ar && (
                  <p
                    className="text-[11.5px] leading-[1.8] mt-2 pt-2"
                    style={{ color: "var(--mj-muted)", borderTop: "1px dashed var(--mj-line)" }}
                  >
                    {flag.note_ar}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* rules */}
          <div
            className="rounded-[14px] px-4 py-3.5"
            style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
          >
            <SideTitle>قوانين المجلس</SideTitle>
            <ol className="mj-rules">
              <li><div><b>الفصحى وحدَها</b><span> — لا نشرَ قبلَ اكتمالِ السلامة</span></div></li>
              <li><div><b>التشكيلُ محمود</b><span> — المنشورُ المشكولُ ينالُ وسامَ «مشكول»</span></div></li>
              <li><div><b>لا ترجماتٍ آليّة</b><span> — اكتبْ بقلمِك ليعلّمَك المدقّق</span></div></li>
            </ol>
          </div>

          {/* real drafts: the user's rejected posts */}
          {drafts.length > 0 && (
            <div
              className="rounded-[14px] px-4 py-3.5"
              style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
            >
              <SideTitle>مسودّاتك</SideTitle>
              {drafts.slice(0, 5).map((draft) => (
                <button
                  key={draft.id}
                  onClick={() => loadDraft(draft)}
                  className="flex items-center gap-2.5 py-2 w-full text-right text-[13.5px]"
                  style={{
                    color: rejectedPostId === draft.id ? "var(--mj-cream)" : "var(--mj-muted)",
                    borderBottom: "1px dashed var(--mj-line)",
                  }}
                >
                  ✎
                  <span className="truncate font-arabic flex-1">{draft.body}</span>
                  <span className="text-[11px] shrink-0 ms-auto">
                    {arabicRelativeTime(draft.created_at)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function SideTitle({ children }) {
  return (
    <div className="flex items-center gap-2 text-[13px] font-bold mb-2" style={{ color: "var(--mj-muted)" }}>
      {children}
      <span className="flex-1 h-px" style={{ background: "var(--mj-line)" }} />
    </div>
  );
}
